import { Products } from '@silvertours/common-landingpages-view';
import { UiLegacy } from '@silvertours/front-legacy-shared';
import { TranslationValues, useFormatter, useTranslations } from 'next-intl';
import {
  Card,
  Description,
  RatingCell,
  Stars,
  Value,
} from './FooterRating.styles';

const MAX_RATING_VALUE = 5;

type Props = {
  rating: NonNullable<Products['aggregateRating']>;
};

/**
 * The visible counterpart of the `AggregateRating` structured data. Google
 * requires that rated values are visible on the page, so this element shows
 * the same values as the JSON-LD.
 */
const FooterRating = ({ rating: { ratingValue, reviewCount } }: Props) => {
  const t = useTranslations('features.navigationLegacy.footer.rating');
  const formatter = useFormatter();

  // The catalogue types every message as plain `string`, so next-intl types
  // calls with values as accepting none (see `BenefitsLegacy/Usp`).
  const translateWithValues = t as unknown as (
    key: Parameters<typeof t>[0],
    values: TranslationValues,
  ) => string;

  const reviewsCount = formatter.number(reviewCount, { useGrouping: true });
  const averageRating = formatter.number(ratingValue, {
    maximumFractionDigits: 1,
  });

  return (
    <RatingCell>
      <Card data-testid="FooterRating">
        <Description>
          {translateWithValues('description', { reviewsCount })}
        </Description>
        <Stars>
          <UiLegacy.Rating
            label={translateWithValues('ratingLabel', {
              value: ratingValue,
              maxValue: MAX_RATING_VALUE,
            })}
            value={ratingValue}
            maxValue={MAX_RATING_VALUE}
            starSize="large"
          />
          <Value aria-hidden="true">
            {averageRating} / {MAX_RATING_VALUE}
          </Value>
        </Stars>
      </Card>
    </RatingCell>
  );
};

export { FooterRating };
